import React, { useContext, useRef } from "react";
import Button from "../Components/Button";
import ReportsLayout from "./ReportsLayout";
import useSalesSummary from "../hooks/useSalesSummary";
import { FilterContext } from "../FilterContextProvider";
import ReactToPrint from "react-to-print";
import cn from "classnames";
import Select from "react-select";
import useDateSelections from "../hooks/useDateSelections";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faSpinner } from "@fortawesome/free-solid-svg-icons";

const ProductInventory = () => {
  const { filterData } = useContext(FilterContext);
  const { productsToRender, resultsLoading, loadError } = useSalesSummary("active");
  const printRef = useRef();
  const { dateOptions, handleDateSelect, renderCustomDateSelector } =
    useDateSelections();

  return (
    <ReportsLayout>
      <div className="mt-8 mx-8 py-10 px-12 bg-white rounded-lg border">
        {productsToRender.length > 0 && (
          <div className="flex justify-end text-sm">
            <ReactToPrint
              trigger={() => <Button className="button" text="Print"></Button>}
              content={() => printRef.current}
            />
          </div>
        )}
        <div className="max-w-max">
          <Link to="/reports">
            <div className="-mt-6 mb-6 font-medium text-black text-sm">
              <FontAwesomeIcon icon={faArrowLeft}></FontAwesomeIcon> Back to
              reports
            </div>
          </Link>
        </div>
        <div className="flex flex-col text-sm w-1/4">
          <span className="text-xs">SELECT DATE RANGE</span>
          <Select
            id="date"
            className="text-xs font-light w-full"
            placeholder="Select date range"
            options={dateOptions}
            onChange={handleDateSelect}
            value={dateOptions?.filter(
              (each) => each.value === filterData.date_button
            )}
          />
        </div>
        <div className="my-4 w-1/2">
          {filterData.date_button === "custom" && renderCustomDateSelector()}
        </div>
        <div ref={printRef} className="mt-8 mx-8 text-black">
          <div className="flex text-base mb-2 border-2 border-black p-2">
            <div className="w-1/3 text-xl font-extrabold mr-4">
              TM Inventory Report
            </div>
            <div className="w-1/3">
              <div className="text-lg flex self-center">
                <div className="mr-2 italic">Artist:</div>
                {filterData.artist_name}
              </div>
            </div>
            <div className=" w-1/3">
              <div className="self-center flex font-normal">
                <div className=" mr-4 italic">End Date:</div>
                {filterData.end_date?.slice(5, 7)}/
                {filterData.end_date?.slice(8, 10)}/
                {filterData.end_date?.slice(0, 4)}
              </div>
            </div>
          </div>
          {loadError ? (
            <div className="w-full flex justify-center py-16 text-red-500">
              Something went wrong. Please refresh the page and try again.
            </div>
          ) : resultsLoading ? (
            <div className="w-full flex justify-center py-16">
              <FontAwesomeIcon icon={faSpinner} spin size="2x" />
            </div>
          ) : (
          <table>
            <thead className="text-xs font-light">
              <tr className="text-left border-b-2 border-black h-11 text-right">
                <td className="w-1/5 text-left">PRODUCT</td>
                <td className="w-thin text-left">SIZE</td>
                <td className="w-thin">CURRENT</td>
                <td className="w-thin">PER SHOW</td>
                <td className="w-thin">PURCHASES</td>
                <td className="w-thin">SOLD</td>
                <td className="w-thin">DISCOUNT</td>
                <td className="w-thin">DAMAGE</td>
                <td className="w-thin">ARTIST COMP</td>
                <td className="w-thin">MGMT COMP</td>
                <td className="w-thin">MAIL ORDER</td>
                <td className="w-thin">TM COMP</td>
                <td className="w-thin">INVENTORY ADJUST</td>
              </tr>
            </thead>
            <tbody>
              {productsToRender.map((productToRender, i) => {
                return (
                  <tr
                    key={productToRender.id}
                    className="border-b border-black text-sm h-9 text-right"
                  >
                    <td id={`product_name_${i}`} className="text-left">
                      {productToRender.name}
                    </td>
                    <td id={`size_${i}`} className="text-left">
                      {productToRender.product_size}
                    </td>
                    <td
                      id={`current_${i}`}
                      className={cn("", {
                        "bg-red-300 rounded-md":
                          productToRender.adjustableEndDateCurrent < 0,
                        "bg-yellow-200 rounded-md":
                          productToRender.adjustableEndDateCurrent /
                            productToRender.per_show <
                            3 && productToRender.adjustableEndDateCurrent >= 0,
                      })}
                    >
                      {productToRender.adjustableEndDateCurrent}
                    </td>
                    <td
                      id={`per_show_${i}`}
                      className={
                        productToRender.sold < 0 ? " bg-red-300 rounded-md" : ""
                      }
                    >
                      {productToRender.per_show
                        ? productToRender.per_show?.toFixed(1)
                        : 0}
                    </td>
                    <td
                      id={`purchases_${i}`}
                      className={
                        productToRender.purchases < 0
                          ? " bg-red-300 rounded-md"
                          : ""
                      }
                    >
                      {productToRender.purchases
                        ? productToRender.purchases
                        : 0}
                    </td>
                    <td
                      id={`sold_${i}`}
                      className={
                        productToRender.sold < 0 ? " bg-red-300 rounded-md" : ""
                      }
                    >
                      {productToRender.sold ? productToRender.sold : 0}
                    </td>
                    <td id={`discount_quantity_${i}`}>
                      {productToRender.discount_quantity
                        ? productToRender.discount_quantity
                        : 0}
                    </td>
                    <td id={`damage_quantity_${i}`}>
                      {productToRender.damage_quantity
                        ? productToRender.damage_quantity
                        : 0}
                    </td>
                    <td id={`band_comp_quantity_${i}`}>
                      {productToRender.band_comp_quantity
                        ? productToRender.band_comp_quantity
                        : 0}
                    </td>
                    <td id={`management_comp_${i}`}>
                      {productToRender.management_comp
                        ? productToRender.management_comp
                        : 0}
                    </td>
                    <td id={`mail_order_${i}`}>
                      {productToRender.mail_order
                        ? productToRender.mail_order
                        : 0}
                    </td>
                    <td id={`ctm_comp_${i}`}>
                      {productToRender.ctm_comp ? productToRender.ctm_comp : 0}
                    </td>
                    <td id={`inventory_adjust_${i}`}>
                      {productToRender.inventory_adjust
                        ? productToRender.inventory_adjust
                        : 0}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          )}
        </div>
      </div>
    </ReportsLayout>
  );
};

export default ProductInventory;
